/**
 * La console d'exploitation — LOT F1.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * UN SEUL ARTEFACT, DEUX PRODUITS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le même bundle sert les théâtres et la console ; c'est l'HÔTE qui décide
 * lequel s'affiche (`hote.ts`). Le routeur de la fondation n'a donc qu'une
 * ligne à connaître de ce module : « si l'on est sur l'hôte de la console,
 * monte `routesConsole` à la place de tout le reste ».
 *
 * Ce n'est pas une superposition d'espaces comme `/admin`, `/scan` et
 * `/portail` : c'est une **substitution**. Les écrans d'un théâtre n'existent
 * pas sur `console.ticket.ilygo.ch`, et réciproquement — exactement comme le
 * backend, où une route de portée `console` répond 404 depuis l'adresse d'un
 * client (`plateforme/hooks.ts`). Les deux couches décident la même chose à
 * partir du même signal, ce qui rend l'incohérence impossible.
 */

import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, type RouteObject } from 'react-router'

import { ChargementPage } from '../../ui'
import { CHEMINS_CONSOLE } from './chemins'
import { ConnexionConsole } from './ConnexionConsole'
import { LayoutConsole } from './LayoutConsole'
import { useSessionConsole } from './api'

/* Les écrans sont chargés à la demande : le personnel d'un théâtre et les
   abonnés téléchargent le même bundle, et n'ont aucune raison d'embarquer les
   écrans d'exploitation dans leur chunk initial. */
const PageAccueil = lazy(async () => ({ default: (await import('./PageAccueil')).PageAccueil }))
const PageTenants = lazy(async () => ({ default: (await import('./PageTenants')).PageTenants }))
const PageWalletTenant = lazy(async () => ({ default: (await import('./PageWalletTenant')).PageWalletTenant }))
const PageSmsTenant=lazy(()=>import('./PageSmsTenant').then(m=>({default:m.PageSmsTenant})))
const PageCrmTenant=lazy(async()=>({default:(await import('./PageCrmTenant')).PageCrmTenant}))
const PageTenant = lazy(async () => ({ default: (await import('./PageTenant')).PageTenant }))
const PageCreation = lazy(async () => ({ default: (await import('./PageCreation')).PageCreation }))
const PageSante = lazy(async () => ({ default: (await import('./PageSante')).PageSante }))
const PageSauvegardes = lazy(async () => ({
  default: (await import('./PageSauvegardes')).PageSauvegardes,
}))
const PageSauvegardeTenant = lazy(async () => ({
  default: (await import('./PageSauvegardeTenant')).PageSauvegardeTenant,
}))
const PageStatistiques = lazy(async () => ({
  default: (await import('./PageStatistiques')).PageStatistiques,
}))
const PageJournal = lazy(async () => ({ default: (await import('./PageJournal')).PageJournal }))
const PageBascule = lazy(async () => ({ default: (await import('./PageBascule')).PageBascule }))
const PageSuperadmins = lazy(async () => ({
  default: (await import('./PageSuperadmins')).PageSuperadmins,
}))
const PageSecurite = lazy(async () => ({ default: (await import('./PageSecurite')).PageSecurite }))
const PageMoyensPaiement = lazy(async () => ({
  default: (await import('./PageMoyensPaiement')).PageMoyensPaiement,
}))
const PagePaiementsTenant = lazy(async () => ({
  default: (await import('./PagePaiementsTenant')).PagePaiementsTenant,
}))

/**
 * Garde d'accès de la console.
 *
 * Confort d'interface, comme les gardes du back-office : l'autorisation qui
 * fait foi est celle du backend (`requireSuperadmin`, en `onRequest` pour que
 * le 401 précède toujours le 422 — NF-03).
 */
function RequireSuperadmin({ children }: { readonly children: ReactNode }) {
  const session = useSessionConsole()
  if (session.isLoading) return <ChargementPage />
  if (session.data == null) return <Navigate to={CHEMINS_CONSOLE.connexion} replace />
  return <>{children}</>
}

function Ecran({ children }: { readonly children: ReactNode }) {
  return <Suspense fallback={<ChargementPage />}>{children}</Suspense>
}

export const routesConsole: RouteObject[] = [
  { path: CHEMINS_CONSOLE.connexion, element: <ConnexionConsole /> },
  {
    path: CHEMINS_CONSOLE.racine,
    element: (
      <RequireSuperadmin>
        <LayoutConsole />
      </RequireSuperadmin>
    ),
    children: [
      { path: 'securite', element: <Ecran><PageSecurite /></Ecran> },
      {
        index: true,
        element: (
          <Ecran>
            <PageAccueil />
          </Ecran>
        ),
      },
      {
        path: 'tenants',
        element: (
          <Ecran>
            <PageTenants />
          </Ecran>
        ),
      },
      {
        path: 'tenants/nouveau',
        element: (
          <Ecran>
            <PageCreation />
          </Ecran>
        ),
      },
      {
        path: 'tenants/:slug',
        element: (
          <Ecran>
            <PageTenant />
          </Ecran>
        ),
      },
      {path: 'tenants/:slug/wallet', element: <Ecran><PageWalletTenant /></Ecran>},
      {path:'tenants/:slug/sms',element:<Ecran><PageSmsTenant/></Ecran>},
      {path:'tenants/:slug/crm',element:<Ecran><PageCrmTenant/></Ecran>},
      {
        path: 'tenants/:slug/paiements',
        element: (
          <Ecran>
            <PagePaiementsTenant />
          </Ecran>
        ),
      },
      {
        path: 'moyens-paiement',
        element: (
          <Ecran>
            <PageMoyensPaiement />
          </Ecran>
        ),
      },
      {
        path: 'sante',
        element: (
          <Ecran>
            <PageSante />
          </Ecran>
        ),
      },
      {
        path: 'sauvegardes',
        element: (
          <Ecran>
            <PageSauvegardes />
          </Ecran>
        ),
      },
      {
        path: 'sauvegardes/:slug',
        element: (
          <Ecran>
            <PageSauvegardeTenant />
          </Ecran>
        ),
      },
      {
        path: 'statistiques',
        element: (
          <Ecran>
            <PageStatistiques />
          </Ecran>
        ),
      },
      {
        path: 'journal',
        element: (
          <Ecran>
            <PageJournal />
          </Ecran>
        ),
      },
      {
        path: 'bascule',
        element: (
          <Ecran>
            <PageBascule />
          </Ecran>
        ),
      },
      {
        path: 'superadmins',
        element: (
          <Ecran>
            <PageSuperadmins />
          </Ecran>
        ),
      },
      /* Aucune route de théâtre n'existe ici : tout ce qui n'est pas reconnu
         retourne à la liste, plutôt que d'afficher une page « introuvable »
         qui laisserait croire qu'un écran manque. */
      { path: '*', element: <Navigate to={CHEMINS_CONSOLE.tenants} replace /> },
    ],
  },
]

export { estHoteConsole, SOUS_DOMAINE_CONSOLE } from './hote'
export { CHEMINS_CONSOLE } from './chemins'
